import { ContentfulLivePreviewProvider } from "@contentful/live-preview/react";
import { LightboxContextProvider } from "@contexts/lightboxContext";
import { QrContextProvider } from "@contexts/qrModalContext";
import { createStore, Provider } from "jotai";
import mixpanelBrowser from "mixpanel-browser";
import localFont from "next/font/local";
import type { AppProps } from "next/app";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import Head from "next/head";
import Script from "next/script";
import React, { useState, useEffect } from "react";
import { useExitIntent } from "use-exit-intent";
import { GlobalStyle } from "@globals/AppGlobalStyle";
import GoogleAnalytics from "@utilsGoogleAnalytics";
import Layout from "@components/Layout";
import ErrorBoundary from "@components/ErrorBoundary";
import MainDropDownContext from "@contexts/MainDropDownContext";
import useIntegrations from "@hooks/useIntegrations";
import { useUTMParameters } from "@hooks/useUTMParameters";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import {
	handleCoverageIframeMessage,
	handleOutageIframeMessage,
} from "@helpers/coverageIframe";
import { Actioner } from "@services/actioner";
import { reportError } from "@utils/errorReporting";
import "./global.css";

const newrelicOptions = {
	init: {
		session_replay: {
			enabled: true,
			block_selector: "",
			mask_text_selector: "",
			sampling_rate: 10.0,
			error_sampling_rate: 100.0,
			mask_all_inputs: false,
			collect_fonts: true,
			inline_images: false,
			inline_stylesheet: true,
			mask_input_options: {
				color: true,
				date: true,
				datetime_local: true,
				email: true,
				month: true,
				number: true,
				range: true,
				search: true,
				tel: true,
				text: true,
				time: true,
				url: true,
				week: true,
				text_area: true,
				select: true,
			},
		},
		distributed_tracing: { enabled: true },
		privacy: { cookies_enabled: true },
		ajax: { deny_list: ["bam.nr-data.net"] },
	},
	info: {
		beacon: "bam.nr-data.net",
		errorBeacon: "bam.nr-data.net",
		licenseKey: process.env.NEXT_PUBLIC_NEWRELIC_LICENSE_KEY || "",
		applicationID: process.env.NEXT_PUBLIC_NEWRELIC_APPLICATION_ID || "",
		sa: 1,
	},
	loader_config: {
		accountID: process.env.NEXT_PUBLIC_NEWRELIC_ACCOUNT_ID,
		trustKey: process.env.NEXT_PUBLIC_NEWRELIC_TRUST_KEY,
		agentID: process.env.NEXT_PUBLIC_NEWRELIC_AGENT_ID,
		licenseKey: process.env.NEXT_PUBLIC_NEWRELIC_LICENSE_KEY,
		applicationID: process.env.NEXT_PUBLIC_NEWRELIC_APPLICATION_ID,
	},
};

const tavirajLight = localFont({
	src: "../public/fonts/Taviraj-Light.ttf",
});
const taviraj = localFont({
	src: "../public/fonts/Taviraj-Regular.ttf",
});
const euclidRegular = localFont({
	src: "../public/fonts/EuclidCircularA-Regular.woff",
});
const euclidMedium = localFont({
	src: "../public/fonts/EuclidCircularA-Medium.woff",
});
const euclidLight = localFont({
	src: "../public/fonts/EuclidCircularA-Light.woff",
});

const excludedPaths = ["/identify"];

const jotaiStore = createStore();

function MyApp({ Component, pageProps }: AppProps) {
	const pathname = usePathname();
	const router = useRouter();
	const hideFreshchatWidget = Boolean(pageProps?.removeChatBubble);

	if (typeof document !== "undefined") {
		document.body.classList.remove("ab-pause-scrolling");
	}

	const { registerHandler, resetState } = useExitIntent();

	useUTMParameters();

	// Note: Quick exit now opens abc.net.au in a new window/tab and replaces current tab
	// No need for sessionStorage redirect logic since we're opening in a new window

	useEffect(() => {
		if (
			typeof window !== "undefined" &&
			process.env.NODE_ENV === "production"
		) {
			(async () => {
				try {
					const { BrowserAgent } = await import(
						"@newrelic/browser-agent/loaders/browser-agent"
					);
					new BrowserAgent(newrelicOptions);
				} catch (error) {
					reportError(error, {
						route: router.asPath,
						component: "MyApp",
						action: "initialize_newrelic_browser_agent",
					});
				}
			})();
		}
	}, [router.asPath]);

	useEffect(() => {
		const handleWindowError = (event: ErrorEvent) => {
			reportError(event.error || event.message, {
				route: router.asPath,
				component: "MyApp",
				action: "window.error",
				metadata: {
					filename: event.filename,
					lineno: event.lineno,
					colno: event.colno,
				},
			});
		};

		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			reportError(event.reason, {
				route: router.asPath,
				component: "MyApp",
				action: "window.unhandledrejection",
			});
		};

		const errorListener = Actioner.addListener(
			Actioner.keyDefs.ERROR,
			(payload) => {
				reportError(payload.error || payload.value || payload, {
					route: router.asPath,
					component: "Actioner",
					action: "error_event",
					metadata: {
						actionKey: payload.actionKey?.label,
						transactionId: payload.transaction_id,
					},
				});
			},
		);

		window.addEventListener("error", handleWindowError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		return () => {
			errorListener.remove();
			window.removeEventListener("error", handleWindowError);
			window.removeEventListener(
				"unhandledrejection",
				handleUnhandledRejection,
			);
		};
	}, [router.asPath]);

	useIntegrations({ hideFreshchatWidget });

	let branchBanner: unknown;
	if (typeof window !== "undefined") {
		branchBanner = window?.document.getElementById("branch-banner-iframe");
	}

	// Modified useEffect in _app.tsx
	useEffect(() => {
		// Function to activate Optimizely with the correct path
		const activateOptimizely = () => {
			if (window.optimizely) {
				// Get the actual URL path the user sees
				const currentPath = window.location.pathname;

				window.optimizely.push({
					type: "activate",
				});
			}
		};

		// Initial activation
		activateOptimizely();

		// Listen for route changes
		const handleRouteChange = (url) => {
			// Allow a small delay for the DOM to update
			setTimeout(activateOptimizely, 50);
		};

		// Subscribe to the router events
		router.events.on("routeChangeComplete", handleRouteChange);

		// Cleanup
		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);

	useEffect(() => {
		if (window.branch) {
			window.branch.closeJourney();
			window.branch.track("pageview");
		}
	}, [Component, pageProps, branchBanner]);

	useEffect(() => {
		mixpanelTrack("page_visited", {
			page_name: pathname,
		});
		if (window?.braze) {
			const user = window.braze.getUser();
			user?.addToCustomAttributeArray("persisted_url_visited", pathname || "");

			// Log Visit Page event for Braze Canvas triggers
			window.braze.logCustomEvent("Visit Page", {
				page_name: pathname,
				page_url: pathname,
			});

			registerHandler({
				id: "brazeExitIntentWeb",
				handler: () => {
					window.braze.logCustomEvent("EXIT INTENT DESKTOP");
				},
			});
			registerHandler({
				id: "brazeExitIntentMobile",
				handler: () => {
					window.braze.logCustomEvent("EXIT INTENT MOBILE");
				},
				context: ["onMobile"],
			});
		}
		return () => {
			resetState();
		};
	}, [pathname, registerHandler, resetState]);

	// Carry over current page query params to all relative links and absolute felixmobile links
	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			if (typeof window === "undefined") return;

			const currentParams = window.location.search;
			if (!currentParams) return;

			const target = e.target as HTMLElement;
			const anchor = target.closest("a");
			if (!anchor) return;

			const href = anchor.getAttribute("href");
			if (!href) return;

			// Match relative links (starting with /) or absolute felixmobile links
			const isRelativeLink = href.startsWith("/");
			const isFelixLink = href.includes("felixmobile.com.au");
			if (!isRelativeLink && !isFelixLink) return;

			const currentSearchParams = new URLSearchParams(currentParams);
			const url = new URL(href, window.location.origin);

			// Only add params that aren't already in the link
			currentSearchParams.forEach((value, key) => {
				if (!url.searchParams.has(key)) {
					url.searchParams.set(key, value);
				}
			});

			const newHref = isFelixLink
				? url.href
				: url.pathname + url.search + url.hash;
			if (newHref !== href) {
				e.preventDefault();
				if (anchor.target === "_blank") {
					window.open(newHref, "_blank", "noopener,noreferrer");
				} else {
					window.location.href = newHref;
				}
			}
		};

		document.addEventListener("click", handleClick, true);
		return () => document.removeEventListener("click", handleClick, true);
	}, []);

	const [MainDropDownData, setMainDropDownData] = useState(
		pageProps.mainDropDownData && pageProps.mainDropdownAppLinks
			? pageProps.mainDropDownData.concat(pageProps.mainDropdownAppLinks)
			: [],
	);

	useEffect(() => {
		const handleIframeMessage = (event) => {
			handleCoverageIframeMessage(event);
			handleOutageIframeMessage(event);
		};

		window.addEventListener("message", handleIframeMessage);
		return () => {
			window.removeEventListener("message", handleIframeMessage);
		};
	}, []);

	const contentfulID = pageProps.sysData?.id;
	const filteredLogo =
		contentfulID !== "696I0Apnqj9kgGvdXX2vlT" && pageProps.logo; //hack to disable the logo for MRTM builds
	const filteredNotificationBarToggle =
		contentfulID !== "696I0Apnqj9kgGvdXX2vlT" &&
		pageProps.notificationBarToggle; //hack to disable the notificationBarToggle for MRTM builds
	const filteredNotificationBar =
		contentfulID !== "696I0Apnqj9kgGvdXX2vlT" && pageProps.notificationBar; //hack to disable the notificationBar for MRTM builds

	//@ts-ignore
	const shouldExcludeLayout = excludedPaths.includes(pathname);

	if (process?.env?.NEXT_PUBLIC_DEBUG_MODE) {
		console.log(
			(mixpanelBrowser as any)?.felixMixpanel?.get_session_replay_url(),
		);
	}

	const appContent =
		pathname === "/buy" || pathname === "/activate/retail" ? (
			<Component {...pageProps} />
		) : (
			<>
				<GlobalStyle />
				{!shouldExcludeLayout && (
					<Layout
						slug={pageProps.relativeUrl}
						logo={filteredLogo}
						notificationBarToggle={filteredNotificationBarToggle}
						notificationBar={filteredNotificationBar}
						buttonText={pageProps?.navigation?.fields?.headlineButtonText}
						buttonColor={pageProps?.navigation?.fields?.headlineButtonColor}
						navigationList={pageProps?.navigation?.fields?.navigationList}
						navigationBackgroundColor={
							pageProps?.navigation?.fields?.backgroundColor
						}
						navigationcontentcolor={pageProps?.navigation?.fields?.contentColor}
						promoCodeToApply={pageProps?.navigation?.fields?.promoCodeToApply}
					>
						<QrContextProvider>
							<ContentfulLivePreviewProvider
								locale="en-US"
								enableLiveUpdates={pageProps.preview}
								enableInspectorMode={pageProps.preview}
								targetOrigin={"https://app.contentful.com"}
							>
								<GoogleAnalytics />
								<Component {...pageProps} />
							</ContentfulLivePreviewProvider>
						</QrContextProvider>
					</Layout>
				)}
				{shouldExcludeLayout && (
					<>
						<Component {...pageProps} />{" "}
					</>
				)}
			</>
		);

	return (
		<Provider store={jotaiStore}>
			<>
				{/* Optimizely Web SDK - Must be outside Head component */}
				{/* <Script
					src="https://cdn.optimizely.com/js/25976052235.js"
					strategy="afterInteractive"
				/> */}

				{/* Optimizely Edge Client - Load first for edge decisions */}
				{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
					<script
						src="https://optimizely.felixmobile.com.au/edge-client/v1/24176680311/6179243931992064"
						referrerPolicy="no-referrer-when-downgrade"
					/>
				)}

				<Head>
					{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
						<>
							<GoogleAnalytics />
							<script
								dangerouslySetInnerHTML={{
									__html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_KEY}');
            `,
								}}
							/>
						</>
					)}
				</Head>

				<div>
					<MainDropDownContext.Provider
						value={{ MainDropDownData, setMainDropDownData }}
					>
						<style jsx global>{`
          :root {
            --euclid-regular: ${euclidRegular.style.fontFamily};
            --euclid-medium: ${euclidMedium.style.fontFamily};
            --euclid-light: ${euclidLight.style.fontFamily};
            --taviraj-light: ${tavirajLight.style.fontFamily};
            --taviraj: ${taviraj.style.fontFamily};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
		  `}</style>
						<LightboxContextProvider>
							<ErrorBoundary route={router.asPath}>{appContent}</ErrorBoundary>

							{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
								<Script
									src="https://wchat.freshchat.com/js/widget.js"
									strategy="afterInteractive"
									onError={(error) => {
										console.error(
											"Script Error loading Freshchat widget script:",
											error,
										);
									}}
								/>
							)}

							{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
								<Script
									id="braze-init"
									strategy="beforeInteractive"
									dangerouslySetInnerHTML={{
										__html: `try {if (window.location.pathname !== "/identify") {
						
								+function(a,p,P,b,y){a.braze={};a.brazeQueue=[];for(var s="BrazeSdkMetadata DeviceProperties Card Card.prototype.dismissCard Card.prototype.removeAllSubscriptions Card.prototype.removeSubscription Card.prototype.subscribeToClickedEvent Card.prototype.subscribeToDismissedEvent Card.fromContentCardsJson Banner CaptionedImage ClassicCard ControlCard ContentCards ContentCards.prototype.getUnviewedCardCount Feed Feed.prototype.getUnreadCardCount ControlMessage InAppMessage InAppMessage.SlideFrom InAppMessage.ClickAction InAppMessage.DismissType InAppMessage.OpenTarget InAppMessage.ImageStyle InAppMessage.Orientation InAppMessage.TextAlignment InAppMessage.CropType InAppMessage.prototype.closeMessage InAppMessage.prototype.removeAllSubscriptions InAppMessage.prototype.removeSubscription InAppMessage.prototype.subscribeToClickedEvent InAppMessage.prototype.subscribeToDismissedEvent InAppMessage.fromJson FullScreenMessage ModalMessage HtmlMessage SlideUpMessage User User.Genders User.NotificationSubscriptionTypes User.prototype.addAlias User.prototype.addToCustomAttributeArray User.prototype.addToSubscriptionGroup User.prototype.getUserId User.prototype.incrementCustomUserAttribute User.prototype.removeFromCustomAttributeArray User.prototype.removeFromSubscriptionGroup User.prototype.setCountry User.prototype.setCustomLocationAttribute User.prototype.setCustomUserAttribute User.prototype.setDateOfBirth User.prototype.setEmail User.prototype.setEmailNotificationSubscriptionType User.prototype.setFirstName User.prototype.setGender User.prototype.setHomeCity User.prototype.setLanguage User.prototype.setLastKnownLocation User.prototype.setLastName User.prototype.setPhoneNumber User.prototype.setPushNotificationSubscriptionType InAppMessageButton InAppMessageButton.prototype.removeAllSubscriptions InAppMessageButton.prototype.removeSubscription InAppMessageButton.prototype.subscribeToClickedEvent FeatureFlag FeatureFlag.prototype.getStringProperty FeatureFlag.prototype.getNumberProperty FeatureFlag.prototype.getBooleanProperty automaticallyShowInAppMessages destroyFeed hideContentCards showContentCards showFeed showInAppMessage toggleContentCards toggleFeed changeUser destroy getDeviceId initialize isPushBlocked isPushPermissionGranted isPushSupported logCardClick logCardDismissal logCardImpressions logContentCardImpressions logContentCardClick logContentCardsDisplayed logCustomEvent logFeedDisplayed logInAppMessageButtonClick logInAppMessageClick logInAppMessageHtmlClick logInAppMessageImpression logPurchase openSession requestPushPermission removeAllSubscriptions removeSubscription requestContentCardsRefresh requestFeedRefresh refreshFeatureFlags requestImmediateDataFlush enableSDK isDisabled setLogger setSdkAuthenticationSignature addSdkMetadata disableSDK subscribeToContentCardsUpdates subscribeToFeedUpdates subscribeToInAppMessage subscribeToSdkAuthenticationFailures toggleLogging unregisterPush wipeData handleBrazeAction subscribeToFeatureFlagsUpdates getAllFeatureFlags".split(" "),i=0;i<s.length;i++){for(var m=s[i],k=a.braze,l=m.split("."),j=0;j<l.length-1;j++)k=k[l[j]];k[l[j]]=(new Function("return function "+m.replace(/\\./g,"_")+"(){window.brazeQueue.push(arguments); return true}"))()}window.braze.getCachedContentCards=function(){return new window.braze.ContentCards};window.braze.getCachedFeed=function(){return new window.braze.Feed};window.braze.getUser=function(){return new window.braze.User};window.braze.getFeatureFlag=function(){return new window.braze.FeatureFlag};(y=p.createElement(P)).type='text/javascript';
								y.src='https://js.appboycdn.com/web-sdk/4.8/braze.min.js';
								y.async=1;(b=p.getElementsByTagName(P)[0]).parentNode.insertBefore(y,b)
								}(window,document,'script');
						
							braze.initialize('${process.env.NEXT_PUBLIC_BRAZE_API_KEY}', {
							  baseUrl: "sdk.fra-01.braze.eu",
							  enableLogging: ${process.env.NODE_ENV !== "production"},
							  allowUserSuppliedJavascript: true,
							});
              
                    braze.openSession();
                    
                    // Dispatch custom event when Braze is ready
                    window.dispatchEvent(new CustomEvent('braze_ready'));
              
                    const user = braze.getUser();
										let previousVisited = null;
										try {
											previousVisited = window.sessionStorage.getItem('visited');
										} catch (error) {
											console.warn('Unable to read sessionStorage for visited flag', error);
										}
              
                    if (!previousVisited) {
                      user.setCustomUserAttribute('url_visited', ['${pathname}']);
											try {
												window.sessionStorage.setItem('visited', true);
											} catch (error) {
												console.warn('Unable to write sessionStorage for visited flag', error);
											}
                    };
                
              
                    braze.subscribeToInAppMessage(function(inAppMessage) {
                      const currentPath = window.location.pathname;
                    
                      if (inAppMessage.extras && inAppMessage.extras.page) {
                        const messagePages = inAppMessage.extras.page.split(',').map(path => path.trim());
                    
                        if (messagePages.includes(currentPath)) {
                          braze.showInAppMessage(inAppMessage);
						  document.body.classList.remove("ab-pause-scrolling");
                        }
                      } else {
                        // Show the message on all pages if there's no 'page' extra
                        braze.showInAppMessage(inAppMessage);
						document.body.classList.remove("ab-pause-scrolling");
                      }
                    });
                    
              
                  } 
} catch (error) {
  console.error('Error initializing Braze:', error);
}`,
									}}
								/>
							)}

							{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
								<Script
									onError={(error) => {
										console.error(
											"Script Error loading Qualtrics script:",
											error,
										);
									}}
									id="qualtrics-init"
									src="https://zn2tzbrdezr1rrlzl-vhavoiceofcustomer.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_2tzBrDEzr1RrLZl"
									strategy="lazyOnload"
									dangerouslySetInnerHTML={{
										__html: `
    (function () {
        var g = function (e, h, f, g) {
            this.get = function (a) { for (var a = a + "=", c = document.cookie.split(";"), b = 0, e = c.length; b < e; b++) { for (var d = c[b]; " " == d.charAt(0);)d = d.substring(1, d.length); if (0 == d.indexOf(a)) return d.substring(a.length, d.length) } return null };
            this.set = function (a, c) { var b = "", b = new Date; b.setTime(b.getTime() + 6048E5); b = "; expires=" + b.toGMTString(); document.cookie = a + "=" + c + b + "; path=/; " };
            this.check = function () { var a = this.get(f); if (a) a = a.split(":"); else if (100 != e) "v" == h && (e = Math.random() >= e / 100 ? 0 : 100), a = [h, e, 0], this.set(f, a.join(":")); else return !0; var c = a[1]; if (100 == c) return !0; switch (a[0]) { case "v": return !1; case "r": return c = a[2] % Math.floor(100 / c), a[2]++, this.set(f, a.join(":")), !c }return !0 };
            this.go = function () { if (this.check()) { var a = document.createElement("script"); a.type = "text/javascript"; a.src = g; document.body && document.body.appendChild(a) } };
            this.start = function () { var t = this; "complete" !== document.readyState ? window.addEventListener ? window.addEventListener("load", function () { t.go() }, !1) : window.attachEvent && window.attachEvent("onload", function () { t.go() }) : t.go() };
        };
        try { (new g(100, "r", "QSI_S_ZN_2tzBrDEzr1RrLZl", "https://zn2tzbrdezr1rrlzl-vhavoiceofcustomer.siteintercept.qualtrics.com/SIE/?Q_ZID=ZN_2tzBrDEzr1RrLZl")).start() } catch (i) { }
    })();
`,
									}}
								/>
							)}

							{!process.env.NEXT_PUBLIC_IS_E2E_TEST && (
								<Script
									id="branch-init"
									onError={(error) => {
										console.error("Script Error loading Branch script:", error);
										window.__branchFailed = true;
										document.dispatchEvent(
											new CustomEvent("branch_failed", { bubbles: true }),
										);
									}}
									dangerouslySetInnerHTML={{
										__html: `
										try {
              (function(b, r, a, n, c, h, _, s, d, k) {
                if (!b[n] || !b[n]._q) {
                  for (; s < _.length; ) c(h, _[s++]);
                  d = r.createElement(a);
                  d.addEventListener("load", function() {
                    var _be = new CustomEvent("branch_loaded", { bubbles: true });
                    r.dispatchEvent(_be);
                  });
                  d.addEventListener("error", function() {
                    b.__branchFailed = true;
                    r.dispatchEvent(new CustomEvent("branch_failed", { bubbles: true }));
                  });
                  d.async = 1;
                  d.src = "https://cdn.branch.io/branch-latest.min.js";
                  k = r.getElementsByTagName(a)[0];
                  k.parentNode.insertBefore(d, k);
                  b[n] = h;
                }
              })(
                window,
                document,
                "script",
                "branch",
                function(b, r) {
                  b[r] = function() {
                    b._q.push([r, arguments]);
                  };
                },
                { _q: [], _v: 1 },
                "addListener applyCode autoAppIndex banner closeBanner closeJourney creditHistory credits data deepview deepviewCta first getCode init link logout redeem referrals removeListener sendSMS setBranchViewData setIdentity track validateCode trackCommerceEvent logEvent disableTracking".split(
                  " "
                ),
                0
              );
              window.branch.init("${process.env.NEXT_PUBLIC_BRANCH_KEY}", {
                disable_entry_animation: true,
                disable_exit_animation: true
              }, function(err) {
                if (err) {
                  window.__branchFailed = true;
                  document.dispatchEvent(new CustomEvent("branch_failed", { bubbles: true }));
                } else {
                  window.__branchInitialized = true;
                  document.dispatchEvent(new CustomEvent("branch_initialized", { bubbles: true }));
                }
              });
} catch (error) {
	console.error("ScriptError initializing Branch", error);
}`,
									}}
									strategy="beforeInteractive"
								/>
							)}
						</LightboxContextProvider>
					</MainDropDownContext.Provider>
				</div>
			</>
		</Provider>
	);
}

export default MyApp;
